
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

/**
 * @typedef {Object} BlogPost
 * @property {string} id
 * @property {string} title
 * @property {string} slug
 * @property {string} excerpt
 * @property {string} image_url
 * @property {string} published_at
 */

/** @type {BlogPost[]} */
const initialPosts = [];

export default function Home() {
  const [latestPosts, setLatestPosts] = useState(initialPosts);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const loadLatestPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) {
        setFetchError(error);
        console.error('Supabase fetch error:', error);
        setLatestPosts([]);
      } else if (data) {
        setLatestPosts(/** @type {BlogPost[]} */ (data));
      }
    } catch (err) {
      setFetchError(err);
      console.error('Unexpected error while loading posts:', err);
      setLatestPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLatestPosts();
  }, []);

  const scrollToNextSection = () => {
    document
      .getElementById('latest-posts')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black">
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url("https://public.readdy.ai/ai/img_res/42709b4d66523a2c96ba6f82b88b366b.jpg")',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 via-transparent to-purple-900/20"></div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-8 text-center">
          <div className="animate-fade-in-up">
            <h1
              className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-red-100 drop-shadow-2xl tracking-wider sm:tracking-widest mb-4 sm:mb-6 filter brightness-110"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              CAPITAL PUNISHMENT
            </h1>
            <div className="w-20 sm:w-32 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto mb-6 sm:mb-8 shadow-red-500/50 shadow-lg"></div>
            <div className="flex justify-center mb-6 sm:mb-8">
              <img
                src="https://static.readdy.ai/image/ff5cddae9cbb9aafa94e3a0f32a5fc4b/8a6b58ae8153daf6411e594b4e00d429.png"
                alt="Forca"
                className="w-20 h-28 sm:w-32 sm:h-40 object-contain filter brightness-90 contrast-125 drop-shadow-2xl hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>

        {/* Quote Section */}
        <div className="absolute bottom-24 sm:bottom-32 left-4 right-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-20 max-w-sm sm:max-w-2xl mx-auto sm:mx-0">
          <blockquote
            className="text-red-200 italic text-sm sm:text-lg leading-relaxed text-center"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            <div className="mb-2 sm:mb-4">
              <i className="ri-double-quotes-l text-red-500 text-xl sm:text-2xl opacity-70"></i>
            </div>
            <p className="mb-2 sm:mb-4 drop-shadow-lg px-2">
              "O truque mais esperto do Diabo é convencer-nos de que não
              existe"
            </p>
            <footer className="text-red-400 text-xs sm:text-sm font-semibold tracking-wider">
              — Charles Baudelaire
            </footer>
          </blockquote>
        </div>

        {/* Scroll‑down Arrow */}
        <div
          className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 cursor-pointer hover:scale-110 transition-transform duration-300"
          onClick={scrollToNextSection}
        >
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 flex items-center justify-center animate-bounce-gentle mb-1 sm:mb-2">
              <i className="ri-arrow-down-line text-red-400 text-xl sm:text-2xl hover:text-red-300 transition-colors duration-300 drop-shadow-lg"></i>
            </div>
            <span className="text-xs text-gray-400 uppercase tracking-wider">
              Descubra
            </span>
          </div>
        </div>
      </section>

      {/* Latest Posts Section */}
      <section
        id="latest-posts"
        className="py-12 sm:py-20 bg-gradient-to-b from-gray-900 to-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2
              className="text-2xl sm:text-4xl md:text-5xl font-bold text-red-100 mb-4 sm:mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Artigos Mais Recentes
            </h2>
            <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto mb-4 sm:mb-6"></div>
          </div>

          {loading ? (
            <div className="text-center py-12 sm:py-20">
              <i className="ri-loader-4-line text-3xl sm:text-4xl text-red-600 animate-spin"></i>
              <p className="mt-4 text-gray-400 text-sm sm:text-base">A carregar artigos...</p>
            </div>
          ) : fetchError ? (
            <div className="text-center py-12 sm:py-20 text-red-500">
              <i className="ri-error-warning-line text-4xl sm:text-6xl mb-4"></i>
              <p className="text-base sm:text-lg px-4">
                Ocorreu um erro ao carregar os artigos. Por favor, tente mais
                tarde.
              </p>
            </div>
          ) : latestPosts.length === 0 ? (
            <div className="text-center py-12 sm:py-20">
              <i className="ri-file-list-line text-4xl sm:text-6xl text-gray-600 mb-4"></i>
              <p className="text-gray-400 text-base sm:text-lg mb-4">
                Ainda não há artigos publicados
              </p>
              <p className="text-gray-500 text-xs sm:text-sm">
                Vá ao painel de administração para criar o seu primeiro artigo
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {latestPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  onClick={handleLinkClick}
                >
                  <article className="group cursor-pointer transform hover:scale-105 transition-all duration-500">
                    <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl border border-red-900/30 hover:border-red-500/50 transition-all duration-300">
                      <div className="relative h-48 sm:h-64 overflow-hidden">
                        {post.image_url ? (
                          <img
                            src={post.image_url}
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                            <i className="ri-image-line text-3xl sm:text-4xl text-gray-500"></i>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      </div>
                      <div className="p-4 sm:p-6">
                        <h3
                          className="text-lg sm:text-xl font-bold text-red-100 mb-2 sm:mb-3 group-hover:text-red-300 transition-colors line-clamp-2"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {post.title}
                        </h3>
                        <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center text-xs text-gray-500">
                          <i className="ri-calendar-line mr-1"></i>
                          <span>
                            {post.published_at
                              ? new Date(post.published_at).toLocaleDateString(
                                  'pt-PT'
                                )
                              : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}

          {/* Link to all posts */}
          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/blog"
              onClick={handleLinkClick}
              className="inline-block bg-gradient-to-r from-red-600 to-red-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:from-red-700 hover:to-red-900 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-red-500/25 border border-red-700/50 text-sm sm:text-base"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              <span className="flex items-center gap-2">
                Ver Todos os Artigos
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
